import { useEffect, useRef, useState } from "react";
import { api, API, fmtAUD, formatErr } from "../lib/api";
import { Save, RotateCw, AlertTriangle, CheckCircle2, Plus, Trash2, Sparkles, Wand2, FlaskConical, Upload, Image as ImageIcon, History, X, Clock, RotateCcw } from "lucide-react";

import imgStraight from "../assets/products/straight-ramp.webp";
import imgWing from "../assets/products/wing-ramp.webp";
import imgInfill from "../assets/products/infill.webp";
import imgStep from "../assets/products/step.webp";

const PRODUCT_IMG_DEFAULTS = {
  straight_ramp: { label: "Straight Rubber Ramp", img: imgStraight },
  wing_ramp:     { label: "Rubber Ramp with Wings", img: imgWing },
  infill:        { label: "Rubber Infill", img: imgInfill },
  step:          { label: "Rubber Step / Platform", img: imgStep },
};

const BACKEND_BASE = (API || "").replace(/\/api$/, "");
const resolveImgUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_BASE}${u}` : u);

// Stable column definitions (avoid inline arrays in JSX — prevents BandTable re-renders)
const COLS_WEIGHT_FACTOR = [{ k: "max_kg", label: "Up to (kg)" }, { k: "cost", label: "Cost ($)" }];
const COLS_WING_COST     = [{ k: "max_mm", label: "Up to (mm)" }, { k: "cost", label: "Cost ($)" }];
const COLS_MAN_LIFT      = [{ k: "max_kg", label: "Up to (kg)" }, { k: "lifts", label: "Man Lifts" }];
const COLS_FABRICATION   = [{ k: "max_kg", label: "Up to (kg)" }, { k: "pieces", label: "Pieces" }];


const TABS = [
  { key: "wing_rates", label: "Wing Ramp" },
  { key: "straight_rates", label: "Straight Ramp" },
  { key: "infill_rates", label: "Infill" },
  { key: "step_rates", label: "Step / Platform" },
  { key: "margins", label: "Margins" },
  { key: "tables", label: "Lookup Tables" },
  { key: "colours", label: "Colours" },
  { key: "calibration", label: "Calibration" },
  { key: "product_images", label: "Product Images" },
];

export default function AdminPricing() {
  const [p, setP] = useState(null);
  const [orig, setOrig] = useState(null);
  const [tab, setTab] = useState("wing_rates");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [bench, setBench] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/pricing");
      setP(r.data); setOrig(JSON.stringify(r.data));
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message || "Failed to load pricing.");
      return;
    }
    try {
      const b = await api.get("/pricing/benchmarks");
      setBench(b.data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("Failed to load wing-ramp benchmarks:", err?.message || err);
      }
    }
  };
  useEffect(() => { load(); }, []);

  if (!p) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]" data-testid="admin-pricing-loading">
        <div className="flex items-center gap-3 text-sm text-[#254391]">
          <RotateCw size={16} className="animate-spin" />
          <span>Loading pricing settings…</span>
        </div>
      </div>
    );
  }
  const dirty = JSON.stringify(p) !== orig;

  const save = async () => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await api.put("/pricing", p);
      setP(r.data); setOrig(JSON.stringify(r.data));
      setMsg("Pricing saved.");
      const b = await api.get("/pricing/benchmarks");
      setBench(b.data);
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const recalibrate = async () => {
    if (!window.confirm("Reset all pricing to factory defaults?")) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/pricing/recalibrate");
      setP(data.pricing); setOrig(JSON.stringify(data.pricing));
      setBench({ benchmarks: data.benchmarks });
      setMsg("Reset to defaults.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const autoCalibrate = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/pricing/auto-calibrate");
      setP(data.pricing); setOrig(JSON.stringify(data.pricing));
      setBench({ benchmarks: data.benchmarks });
      setMsg("Auto-calibrated rates from Excel benchmarks.");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const setNum = (k) => (e) => setP({ ...p, [k]: Number(e.target.value || 0) });
  const setMargin = (k, v) => setP({ ...p, margins: { ...(p.margins || {}), [k]: Number(v) / 100 } });

  const updRow = (key, idx, field, val) => {
    const arr = Array.isArray(p[key]) ? [...p[key]] : [];
    if (!arr[idx]) return;
    arr[idx] = { ...arr[idx], [field]: Number(val) };
    setP({ ...p, [key]: arr });
  };
  const addRow = (key, template) => {
    const arr = Array.isArray(p[key]) ? p[key] : [];
    setP({ ...p, [key]: [...arr, template] });
  };
  const delRow = (key, idx) => {
    const arr = Array.isArray(p[key]) ? p[key] : [];
    setP({ ...p, [key]: arr.filter((_, i) => i !== idx) });
  };
  const updColour = (i, field, val) => {
    const arr = Array.isArray(p.colours) ? [...p.colours] : [];
    if (!arr[i]) return;
    arr[i] = { ...arr[i], [field]: val };
    setP({ ...p, colours: arr });
  };

  return (
    <div className="space-y-5" data-testid="admin-pricing-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#fda500] font-bold mb-1">Admin · Pricing Settings</div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0A1128]">Rates &amp; Calibration</h1>
          <p className="text-sm text-gray-500 mt-1">Edit pricing tables. Changes are saved permanently to the database.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowHistory(true)} disabled={busy} className="px-3.5 py-2 text-sm border border-gray-300 hover:border-[#254391] hover:text-[#254391] rounded-md flex items-center gap-1.5" data-testid="pricing-history-btn">
            <History size={13} /> Version History
          </button>
          <button onClick={recalibrate} disabled={busy} className="px-3.5 py-2 text-sm border border-gray-300 hover:border-[#254391] hover:text-[#254391] rounded-md flex items-center gap-1.5">
            <RotateCw size={13} /> Reset Defaults
          </button>
          <button onClick={autoCalibrate} disabled={busy} className="px-3.5 py-2 text-sm bg-blue-50 text-[#254391] border border-blue-200 hover:bg-blue-100 rounded-md flex items-center gap-1.5" data-testid="auto-calibrate-btn">
            <Wand2 size={13} /> Auto-Calibrate to Excel
          </button>
          <button onClick={save} disabled={!dirty || busy} className="px-4 py-2 text-sm bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] font-bold rounded-md flex items-center gap-1.5 ezy-card disabled:opacity-50 disabled:cursor-not-allowed transition-all" data-testid="pricing-save-btn">
            <Save size={13} /> {busy ? "Saving…" : dirty ? "Save Changes" : "Saved"}
          </button>
        </div>
      </div>

      {/* Last updated metadata */}
      {(p.updated_at || p.updated_by) && (
        <div className="bg-blue-50/50 border border-blue-200 rounded-md px-4 py-2.5 text-xs flex items-center gap-2 flex-wrap" data-testid="pricing-meta">
          <Clock size={12} className="text-[#254391]" />
          <span className="text-gray-600">Last updated</span>
          {p.updated_at && <span className="font-mono text-[#0A1128]" data-testid="pricing-updated-at">{new Date(p.updated_at).toLocaleString("en-AU")}</span>}
          {p.updated_by && p.updated_by.name && (
            <>
              <span className="text-gray-400">by</span>
              <span className="font-semibold text-[#254391]" data-testid="pricing-updated-by">{p.updated_by.name}</span>
              <span className="text-gray-400 text-[10px] uppercase tracking-wider">({p.updated_by.role})</span>
            </>
          )}
        </div>
      )}

      {err && <Banner type="err">{err}</Banner>}
      {msg && <Banner type="ok">{msg}</Banner>}

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-xl ezy-card overflow-hidden">
        <div className="flex gap-0 border-b border-gray-200 overflow-x-auto bg-gradient-to-b from-gray-50/50 to-white">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-5 py-3.5 text-sm font-semibold whitespace-nowrap transition-all relative ${
                tab === t.key
                  ? "text-[#254391] bg-white"
                  : "text-gray-500 hover:text-[#254391] hover:bg-white/60"
              }`}
            >
              {t.label}
              {tab === t.key && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#fda500] rounded-t-full" />}
            </button>
          ))}
        </div>

        <div className="p-5">
          {/* WING RAMP RATES — uses top-level pricing fields */}
          {tab === "wing_rates" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="wing-rates-tab">
              <Num label="Density (kg/m³)"      v={p.density_kg_per_m3}     onCh={setNum("density_kg_per_m3")} step="0.01" tid="cfg-density" />
              <Num label="Wing Volume Factor"   v={p.wing_volume_factor}    onCh={setNum("wing_volume_factor")} step="0.0001" />
              <Num label="$/kg Material Rate"   v={p.material_rate_per_kg}  onCh={setNum("material_rate_per_kg")} step="0.001" tid="cfg-material-rate" />
              <Num label="Fixed per Job ($)"    v={p.fixed_per_job}         onCh={setNum("fixed_per_job")} step="0.01" tid="cfg-fixed" />
              <Num label="Contingency (%)"      v={(p.contingency_pct || 0) * 100} onCh={(e)=>setP({...p, contingency_pct: Number(e.target.value)/100})} step="0.01" tid="cfg-contingency" />
              <Num label="Pallet Threshold (kg)" v={p.pallet_threshold_kg}  onCh={setNum("pallet_threshold_kg")} />
              <Num label="Pallet Cost ($)"      v={p.pallet_cost}           onCh={setNum("pallet_cost")} step="0.01" tid="cfg-pallet" />
              <Num label="Colour Surcharge (%)" v={(p.colour_surcharge_pct || 0) * 100} onCh={(e)=>setP({...p, colour_surcharge_pct: Number(e.target.value)/100})} step="0.1" tid="cfg-colour" />
              <Num label="Curved Template ($)"  v={p.curved_template_cost}  onCh={setNum("curved_template_cost")} step="0.01" />
              <Num label="Curved Add. (%)"      v={(p.curved_additional_pct || 0) * 100} onCh={(e)=>setP({...p, curved_additional_pct: Number(e.target.value)/100})} step="0.1" />
              <Num label="Kerb Cost Each ($)"   v={p.kerb_cost_each}        onCh={setNum("kerb_cost_each")} step="0.01" tid="cfg-kerb" />
              <Num label="Notch Cost Each ($)"  v={p.notch_cost_each}       onCh={setNum("notch_cost_each")} step="0.01" />
              <Num label="WVS Cost Each ($)"    v={p.wvs_cost_each}         onCh={setNum("wvs_cost_each")} step="0.01" />
              <Num label="Install Labour $/hr"  v={p.install_labour_rate_per_hr} onCh={setNum("install_labour_rate_per_hr")} step="0.01" />
              <Num label="Install Base Hours"   v={p.install_base_hours}    onCh={setNum("install_base_hours")} step="0.01" />
              <Num label="Install Hours/kg"     v={p.install_hours_per_kg}  onCh={setNum("install_hours_per_kg")} step="0.001" />
            </div>
          )}

          {/* PER-PRODUCT RATES TABS */}
          {tab === "straight_rates" && <ProductRatesTab p={p} setP={setP} prodKey="straight_ramp" />}
          {tab === "infill_rates"   && <ProductRatesTab p={p} setP={setP} prodKey="infill" />}
          {tab === "step_rates"     && <ProductRatesTab p={p} setP={setP} prodKey="step" />}

          {/* MARGINS */}
          {tab === "margins" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="margins-tab">
              <Num label="Wholesale Margin (%)"       v={(p.margins?.wholesale ?? 0) * 100}      onCh={(e)=>setMargin("wholesale", e.target.value)}      tid="margin-wholesale" />
              <Num label="Retail Margin (%)"          v={(p.margins?.retail ?? 0) * 100}         onCh={(e)=>setMargin("retail", e.target.value)}         tid="margin-retail" />
              <Num label="Reseller Margin (%)"        v={(p.margins?.reseller ?? 0) * 100}       onCh={(e)=>setMargin("reseller", e.target.value)}       tid="margin-reseller" />
              <Num label="Supply & Install Margin (%)" v={(p.margins?.supply_install ?? p.margins?.mam_supply_install ?? 0) * 100} onCh={(e)=>setMargin("supply_install", e.target.value)} tid="margin-supply" />
            </div>
          )}

          {/* TABLES */}
          {tab === "tables" && (
            <div className="space-y-6" data-testid="tables-tab">
              <Section title="Weight Factor (kg → $)">
                <BandTable rows={p.weight_factor_table} cols={COLS_WEIGHT_FACTOR}
                  onChange={(i,f,v)=>updRow("weight_factor_table",i,f,v)}
                  onDel={(i)=>delRow("weight_factor_table",i)}
                  onAdd={()=>addRow("weight_factor_table",{max_kg:0,cost:0})} tid="weight-factor" />
              </Section>
              <Section title="Wing Labour (mm → $)">
                <BandTable rows={p.wing_cost_table} cols={COLS_WING_COST}
                  onChange={(i,f,v)=>updRow("wing_cost_table",i,f,v)}
                  onDel={(i)=>delRow("wing_cost_table",i)}
                  onAdd={()=>addRow("wing_cost_table",{max_mm:0,cost:0})} tid="wing-cost" />
              </Section>
              <Section title="Man Lift Rules">
                <BandTable rows={p.man_lift_table} cols={COLS_MAN_LIFT}
                  onChange={(i,f,v)=>updRow("man_lift_table",i,f,v)}
                  onDel={(i)=>delRow("man_lift_table",i)}
                  onAdd={()=>addRow("man_lift_table",{max_kg:0,lifts:0})} tid="man-lift" />
              </Section>
              <Section title="Fabrication Pieces">
                <BandTable rows={p.fabrication_table} cols={COLS_FABRICATION}
                  onChange={(i,f,v)=>updRow("fabrication_table",i,f,v)}
                  onDel={(i)=>delRow("fabrication_table",i)}
                  onAdd={()=>addRow("fabrication_table",{max_kg:0,pieces:0})} tid="fab-pieces" />
              </Section>
            </div>
          )}

          {/* COLOURS */}
          {tab === "colours" && (
            <div data-testid="colours-tab">
              <p className="text-sm text-gray-500 mb-3">Toggle which colours apply the colour surcharge ({((p.colour_surcharge_pct || 0)*100).toFixed(0)}% of material+labour). Natural Black should remain off.</p>
              {(!Array.isArray(p.colours) || p.colours.length === 0) ? (
                <EmptyHint>No colours configured yet. Use "Auto-Calibrate to Excel" to seed defaults.</EmptyHint>
              ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-gray-600 border-b border-gray-200">
                    <th className="px-3 py-2 font-semibold">Colour</th>
                    <th className="px-3 py-2 font-semibold">Apply Surcharge</th>
                    <th className="px-3 py-2 font-semibold">Effect</th>
                  </tr>
                </thead>
                <tbody>
                  {p.colours.map((c, i) => (
                    <tr key={c.name || `colour-${i}`} className="border-b border-gray-100">
                      <td className="px-3 py-2.5 font-medium">{c.name}</td>
                      <td className="px-3 py-2.5">
                        <input type="checkbox" checked={!!c.surcharge} onChange={(e)=>updColour(i,"surcharge",e.target.checked)} data-testid={`colour-surcharge-${i}`} className="w-4 h-4 accent-[#254391]" />
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-500">
                        {c.surcharge ? `+${((p.colour_surcharge_pct||0)*100).toFixed(0)}% on material+labour` : "No surcharge"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          )}

          {/* CALIBRATION */}
          {tab === "calibration" && (
            <CalibrationTab bench={bench} autoCalibrate={autoCalibrate} busy={busy} />
          )}

          {/* PRODUCT IMAGES */}
          {tab === "product_images" && (
            <ProductImagesTab onMsg={(m)=>{setMsg(m); setTimeout(()=>setMsg(""),2500);}} onErr={setErr} />
          )}
        </div>
      </div>

      {showHistory && (
        <PricingHistoryModal
          onClose={() => setShowHistory(false)}
          onRestored={(restored) => {
            setP(restored);
            setOrig(JSON.stringify(restored));
            setMsg("Pricing restored from history.");
            setTimeout(() => setMsg(""), 3000);
            setShowHistory(false);
          }}
        />
      )}
    </div>
  );
}

function PricingHistoryModal({ onClose, onRestored }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/pricing/history");
        setList(r.data || []);
      } catch (e) {
        setErr(formatErr(e.response?.data?.detail) || e.message);
        setList([]);
      }
    })();
  }, []);

  const restore = async (id) => {
    if (!window.confirm("Restore this version? The current pricing will be archived to history so you can roll back again.")) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post(`/pricing/history/${id}/restore`);
      onRestored?.(r.data);
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 pt-12" onClick={onClose} data-testid="pricing-history-modal">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold uppercase tracking-[0.14em] text-[#254391]">Pricing Version History</div>
            <div className="text-xs text-gray-500 mt-0.5">Last 50 saves · restore any prior version</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close" data-testid="pricing-history-close">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {err && <div className="m-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-xs flex items-start gap-2"><AlertTriangle size={13} className="mt-0.5" />{err}</div>}
          {list === null && <div className="p-6 text-sm text-gray-500">Loading history…</div>}
          {list && list.length === 0 && <div className="p-6 text-sm text-gray-500">No prior versions yet. Save a pricing change and a new version will appear here.</div>}
          {list && list.length > 0 && (
            <ul className="divide-y divide-gray-100">
              {list.map((h) => (
                <li key={h.id} className="px-5 py-4 flex items-center justify-between gap-4 hover:bg-blue-50/30" data-testid={`pricing-history-row-${h.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-mono text-[#0A1128]">{h.snapshot_at ? new Date(h.snapshot_at).toLocaleString("en-AU") : "—"}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Snapshot taken when {h.snapshot_by?.name || "an admin"} ({h.snapshot_by?.role || "?"}) saved a change.
                      {h.previous_updated_by?.name && (
                        <> Previous edit by <span className="font-semibold text-[#254391]">{h.previous_updated_by.name}</span>.</>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => restore(h.id)}
                    disabled={busy}
                    data-testid={`pricing-history-restore-${h.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-[#fda500] hover:bg-[#e59500] text-[#0A1128] rounded-md whitespace-nowrap disabled:opacity-50"
                  >
                    <RotateCcw size={12} /> Restore
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductRatesTab({ p, setP, prodKey }) {
  const sp = (p.products && p.products[prodKey]) || {};
  const setSP = (k, v) => setP({ ...p, products: { ...p.products, [prodKey]: { ...sp, [k]: v } } });
  const setSPNum = (k) => (e) => setSP(k, Number(e.target.value || 0));
  return (
    <div>
      <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider">Editing rates for: <span className="text-[#254391] font-semibold">{prodKey.replace("_"," ")}</span></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Num label="Density (kg/m³)"      v={sp.density_kg_per_m3 || 770.27} onCh={setSPNum("density_kg_per_m3")} step="0.01" tid={`${prodKey}-density`} />
        <Num label="$/kg Material Rate"   v={sp.material_rate_per_kg || 0}   onCh={setSPNum("material_rate_per_kg")} step="0.001" tid={`${prodKey}-rate`} />
        <Num label="Fixed per Job ($)"    v={sp.fixed_per_job || 0}          onCh={setSPNum("fixed_per_job")} step="0.01" tid={`${prodKey}-fixed`} />
        <Num label="Contingency (%)"      v={(sp.contingency_pct || 0) * 100} onCh={(e)=>setSP("contingency_pct", Number(e.target.value)/100)} step="0.01" tid={`${prodKey}-cont`} />
        <Num label="Colour Surcharge (%)" v={(sp.colour_surcharge_pct || 0) * 100} onCh={(e)=>setSP("colour_surcharge_pct", Number(e.target.value)/100)} step="0.1" />
        <Num label="Pallet Threshold (kg)" v={sp.pallet_threshold_kg || 30}  onCh={setSPNum("pallet_threshold_kg")} />
        <Num label="Pallet Cost ($)"      v={sp.pallet_cost || 0}            onCh={setSPNum("pallet_cost")} step="0.01" />
        {prodKey === "straight_ramp" && (
          <>
            <Num label="Kerb Cost Each ($)"  v={sp.kerb_cost_each || 0}     onCh={setSPNum("kerb_cost_each")} step="0.01" />
            <Num label="Notch Cost Each ($)" v={sp.notch_cost_each || 0}    onCh={setSPNum("notch_cost_each")} step="0.01" />
            <Num label="WVS Cost Each ($)"   v={sp.wvs_cost_each || 0}      onCh={setSPNum("wvs_cost_each")} step="0.01" />
            <Num label="Curved Add. (%)"     v={(sp.curved_additional_pct || 0) * 100} onCh={(e)=>setSP("curved_additional_pct", Number(e.target.value)/100)} step="0.1" />
          </>
        )}
        {prodKey === "infill" && (
          <Num label="Fab per Cut-out ($)"   v={sp.fabrication_per_cutout || 0} onCh={setSPNum("fabrication_per_cutout")} step="0.01" />
        )}
        {prodKey === "step" && (
          <>
            <Num label="Per-step Fixed ($)"  v={sp.per_step_fixed || 0}     onCh={setSPNum("per_step_fixed")} step="0.01" />
            <Num label="Platform Fixed ($)"  v={sp.platform_fixed || 0}     onCh={setSPNum("platform_fixed")} step="0.01" />
            <Num label="Kerb Cost Each ($)"  v={sp.kerb_cost_each || 0}     onCh={setSPNum("kerb_cost_each")} step="0.01" />
            <Num label="Notch Cost Each ($)" v={sp.notch_cost_each || 0}    onCh={setSPNum("notch_cost_each")} step="0.01" />
          </>
        )}
        <Num label="Install Labour $/hr"  v={sp.install_labour_rate_per_hr || 80} onCh={setSPNum("install_labour_rate_per_hr")} step="0.01" />
        <Num label="Install Base Hours"   v={sp.install_base_hours || 0}     onCh={setSPNum("install_base_hours")} step="0.01" />
        <Num label="Install Hours/kg"     v={sp.install_hours_per_kg || 0}   onCh={setSPNum("install_hours_per_kg")} step="0.001" />
      </div>
      <div className="mt-5 pt-4 border-t border-gray-200">
        <div className="text-xs font-bold uppercase tracking-wider text-[#254391] mb-3">Margins for {prodKey.replace("_"," ")}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["wholesale","retail","reseller","supply_install"].map((k) => (
            <Num key={k} label={`${k.replace("_"," ")} (%)`} v={(sp.margins?.[k] || 0) * 100}
              onCh={(e)=>setSP("margins", { ...sp.margins, [k]: Number(e.target.value)/100 })} step="0.1" tid={`${prodKey}-margin-${k}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CalibrationTab({ bench, autoCalibrate, busy }) {
  const [strBench, setStrBench] = useState(null);
  useEffect(() => {
    api.get("/pricing/benchmarks/straight_ramp").then((r) => setStrBench(r.data));
  }, []);
  const autoCalStraight = async () => {
    try {
      const { data } = await api.post("/pricing/auto-calibrate/straight_ramp");
      setStrBench({ benchmarks: data.benchmarks });
    } catch (e) { alert(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6" data-testid="calibration-tab">
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="text-base font-bold text-[#0A1128] flex items-center gap-2"><FlaskConical size={16} className="text-[#fda500]" /> Wing Ramp Benchmarks</div>
            <p className="text-sm text-gray-500 mt-0.5">Both benchmarks must PASS for accurate Wing Ramp pricing.</p>
          </div>
          <button onClick={autoCalibrate} disabled={busy} className="px-4 py-2 bg-[#254391] hover:bg-[#1E3675] text-white text-sm font-semibold rounded-md flex items-center gap-2 shadow-md" data-testid="auto-calibrate-btn-2">
            <Sparkles size={14} /> Auto-Calibrate Wing Ramp
          </button>
        </div>
        {bench?.benchmarks?.map((b, idx) => (<BenchCard key={idx} b={b} idx={idx + 1} kind="wing" />))}
      </div>
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3 pt-4 border-t border-gray-200">
          <div>
            <div className="text-base font-bold text-[#0A1128] flex items-center gap-2"><FlaskConical size={16} className="text-[#fda500]" /> Straight Ramp Benchmarks</div>
            <p className="text-sm text-gray-500 mt-0.5">5 calibration tests from your Excel sheet.</p>
          </div>
          <button onClick={autoCalStraight} disabled={busy} className="px-4 py-2 bg-[#254391] hover:bg-[#1E3675] text-white text-sm font-semibold rounded-md flex items-center gap-2 shadow-md" data-testid="auto-calibrate-straight-btn">
            <Sparkles size={14} /> Auto-Calibrate Straight Ramp
          </button>
        </div>
        {strBench?.benchmarks?.map((b, idx) => (<BenchCard key={idx} b={b} idx={idx + 1} kind="straight" />))}
      </div>
    </div>
  );
}

function BenchCard({ b, idx, kind = "wing" }) {
  const items = [];
  for (const k of Object.keys(b.expected)) {
    if (k === "actual_only") continue;
    const exp = b.expected[k];
    const act = b.actual[k];
    const diff = act - exp;
    const tol = k === "weight_kg" ? 0.05 : 1.0;
    const passed = Math.abs(diff) <= tol;
    items.push({ k, exp, act, diff, passed });
  }
  const allPass = items.length === 0 ? true : items.every((i) => i.passed);
  return (
    <div className={`rounded-lg border-2 overflow-hidden ${allPass ? "border-green-300 bg-green-50/30" : "border-red-300 bg-red-50/30"}`} data-testid={`bench-card-${idx}`}>
      <div className={`px-5 py-3 flex items-center justify-between ${allPass ? "bg-green-100" : "bg-red-100"}`}>
        <div className="flex items-center gap-2">
          {allPass ? <CheckCircle2 size={18} className="text-green-700" /> : <AlertTriangle size={18} className="text-red-700" />}
          <div>
            <div className="text-sm font-bold text-[#0A1128]">Benchmark {idx}: {b.name}</div>
            <div className="text-xs text-gray-600">
              {b.inputs.width}×{b.inputs.depth} mm
              {b.inputs.left_wing ? `, wings ${b.inputs.left_wing}/${b.inputs.right_wing}` : ", no wings"}
              {", rises "}{b.inputs.left_rise}/{b.inputs.right_rise}
            </div>
          </div>
        </div>
        <span className={`text-xs uppercase tracking-wider font-bold px-3 py-1 rounded ${allPass ? "bg-green-700 text-white" : "bg-red-700 text-white"}`}>
          {allPass ? "PASS" : "FAIL"}
        </span>
      </div>
      <div className="p-4">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-600 border-b border-gray-200">
              <th className="py-2">Metric</th>
              <th className="py-2 text-right">Expected</th>
              <th className="py-2 text-right">Actual</th>
              <th className="py-2 text-right">Diff</th>
              <th className="py-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.k} className="border-b border-gray-100">
                <td className="py-2 font-medium uppercase text-xs tracking-wider">{it.k.replace("_", " ")}</td>
                <td className="py-2 text-right font-mono">{it.k === "weight_kg" ? `${it.exp} kg` : fmtAUD(it.exp)}</td>
                <td className="py-2 text-right font-mono">{it.k === "weight_kg" ? `${it.act} kg` : fmtAUD(it.act)}</td>
                <td className={`py-2 text-right font-mono ${Math.abs(it.diff) <= (it.k === "weight_kg" ? 0.05 : 0.5) ? "text-green-700" : "text-red-700"}`}>
                  {it.diff >= 0 ? "+" : ""}{it.diff.toFixed(2)}
                </td>
                <td className="py-2 text-center">
                  {it.passed ? <CheckCircle2 size={16} className="text-green-600 inline" /> : <AlertTriangle size={16} className="text-red-600 inline" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.14em] text-[#254391] mb-2">{title}</div>
      {children}
    </div>
  );
}

function EmptyHint({ children }) {
  return (
    <div className="bg-blue-50/50 border border-blue-200 text-[#254391] rounded-md px-4 py-3 text-xs flex items-start gap-2" data-testid="empty-hint">
      <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}
function Num({ label, v, onCh, step = "any", tid }) {
  // Wraps the existing parent's `onCh` signature (event object) so we can also support empty.
  // Guard against NaN/undefined/null/"" so React never receives 'NaN' as the value attribute.
  const display = (v === "" || v === null || v === undefined || (typeof v === "number" && !Number.isFinite(v))) ? "" : v;
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-600 mb-1">{label}</label>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        value={display}
        placeholder={`Enter ${label.toLowerCase()}`}
        onFocus={(e) => e.target.select()}
        onChange={onCh}
        data-testid={tid}
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:border-[#254391] focus:ring-2 focus:ring-[#254391]/20 outline-none transition"
      />
    </div>
  );
}
function Banner({ type, children }) {
  const cls = type === "err" ? "bg-red-50 text-red-800 border-red-200" : "bg-green-50 text-green-800 border-green-200";
  const Icon = type === "err" ? AlertTriangle : CheckCircle2;
  return <div className={`flex items-center gap-2 px-4 py-2.5 border ${cls} text-sm rounded-md`}><Icon size={14} /> {children}</div>;
}
function BandTable({ rows, cols, onChange, onDel, onAdd, tid }) {
  // Defensive: handle null/undefined/non-array/empty without crashing.
  const safeRows = Array.isArray(rows) ? rows : [];
  if (safeRows.length === 0) {
    return (
      <div className="space-y-2" data-testid={`${tid}-empty`}>
        <EmptyHint>
          No lookup data available yet. {onAdd ? 'Click "Add band" below to create the first row.' : 'Use "Auto-Calibrate to Excel" to seed default lookup tables.'}
        </EmptyHint>
        {onAdd && (
          <button onClick={onAdd} className="mt-1 text-xs px-3 py-1.5 border border-gray-300 hover:border-[#254391] hover:text-[#254391] rounded-md flex items-center gap-1.5" data-testid={`${tid}-add`}>
            <Plus size={12} /> Add band
          </button>
        )}
      </div>
    );
  }
  return (
    <>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left text-[10px] uppercase tracking-wider text-gray-600 border-b border-gray-200">
            {cols.map(c => <th key={c.k} className="px-3 py-2 font-semibold">{c.label}</th>)}
            {onDel && <th className="px-3 py-2"></th>}
          </tr>
        </thead>
        <tbody>
          {safeRows.map((r, i) => (
            <tr key={`band-${tid}-${i}`} className="border-b border-gray-100">
              {cols.map(c => (
                <td key={c.k} className="px-3 py-1.5">
                  <input type="number" step="any" value={r?.[c.k] ?? ""} onChange={(e)=>onChange(i,c.k,e.target.value)} data-testid={`${tid}-${i}-${c.k}`}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-mono focus:border-[#254391] outline-none" />
                </td>
              ))}
              {onDel && (
                <td className="px-3 py-1.5">
                  <button onClick={() => onDel(i)} className="text-red-500 hover:text-red-700" data-testid={`${tid}-del-${i}`}><Trash2 size={14} /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {onAdd && <button onClick={onAdd} className="mt-2 text-xs px-3 py-1.5 border border-gray-300 hover:border-[#254391] hover:text-[#254391] rounded-md flex items-center gap-1.5" data-testid={`${tid}-add`}><Plus size={12} /> Add band</button>}
    </>
  );
}


function ProductImagesTab({ onMsg, onErr }) {
  const [imgs, setImgs] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/product-images");
      setImgs(r.data || {});
    } catch (e) {
      onErr(formatErr(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (productKey, file) => {
    if (!file) return;
    setBusy(productKey); onErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/product-images/${productKey}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImgs(r.data || {});
      onMsg(`Updated image for ${PRODUCT_IMG_DEFAULTS[productKey].label}.`);
    } catch (e) {
      onErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  const reset = async (productKey) => {
    if (!window.confirm(`Reset ${PRODUCT_IMG_DEFAULTS[productKey].label} image to the default?`)) return;
    setBusy(productKey); onErr("");
    try {
      const r = await api.delete(`/admin/product-images/${productKey}`);
      setImgs(r.data || {});
      onMsg(`Reset image for ${PRODUCT_IMG_DEFAULTS[productKey].label}.`);
    } catch (e) {
      onErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="product-images-tab">
      <p className="text-sm text-gray-500 mb-4">
        Upload custom product images displayed on the Product Selector dashboard. Recommended: square or 16:9 aspect, &lt;5&nbsp;MB, formats <span className="font-mono">.webp / .png / .jpg</span>. Reset to revert to the bundled default.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(PRODUCT_IMG_DEFAULTS).map(([key, def]) => (
          <ProductImageCard
            key={key}
            productKey={key}
            label={def.label}
            defaultImg={def.img}
            customUrl={imgs[key]}
            busy={busy === key}
            onUpload={(f) => upload(key, f)}
            onReset={() => reset(key)}
          />
        ))}
      </div>
    </div>
  );
}

function ProductImageCard({ productKey, label, defaultImg, customUrl, busy, onUpload, onReset }) {
  const inputRef = useRef(null);
  const isCustom = !!customUrl;
  const src = isCustom ? resolveImgUrl(customUrl) : defaultImg;
  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid={`product-image-card-${productKey}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#fda500] font-bold mb-0.5">{productKey.replace("_", " ")}</div>
          <div className="text-sm font-bold text-[#0A1128]">{label}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${isCustom ? "bg-[#254391]/10 text-[#254391]" : "bg-gray-100 text-gray-500"}`}>
          {isCustom ? "Custom" : "Default"}
        </span>
      </div>
      <div className="aspect-[16/9] bg-gray-50 rounded-md overflow-hidden border border-gray-100 mb-3 flex items-center justify-center">
        <img
          src={src}
          alt={label}
          className="max-w-full max-h-full object-contain p-3"
          onError={(e) => { if (e.currentTarget.src !== defaultImg) e.currentTarget.src = defaultImg; }}
          data-testid={`product-image-preview-${productKey}`}
        />
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(f); }}
        data-testid={`product-image-input-${productKey}`}
      />
      <div className="flex gap-2">
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="flex-1 px-3 py-2 text-sm bg-[#254391] hover:bg-[#1E3675] disabled:opacity-50 text-white font-semibold rounded-md flex items-center justify-center gap-1.5"
          data-testid={`product-image-upload-${productKey}`}
        >
          <Upload size={13} /> {busy ? "Uploading…" : (isCustom ? "Replace Image" : "Upload Image")}
        </button>
        <button
          onClick={onReset}
          disabled={busy || !isCustom}
          className="px-3 py-2 text-sm border border-gray-300 hover:border-[#254391] hover:text-[#254391] rounded-md flex items-center gap-1.5 disabled:opacity-40"
          data-testid={`product-image-reset-${productKey}`}
        >
          <RotateCw size={13} /> Reset
        </button>
      </div>
    </div>
  );
}
